import type { AuthedAxiosRequests } from 'features/auth'
import { evalEventIdentity } from 'features/evaluations/metadata/utils/evalEventIndex'
import {
  parseRolodexCount,
  parseRolodexRows,
} from 'features/evaluations/metadata/utils/parseRolodexLoad'
import {
  type DocumentPageMetadataUserEditEntry,
  DocumentPageMetadataUserEditEntrySchema,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { MetadataUserEditLoadOptions } from 'features/knowledgeDocumentViewer/utils/documentEntryLookups'
import { postFetcher } from 'features/rolodex/fetchers'

export async function loadEvalEvents(args: {
  axios: AuthedAxiosRequests
  rolodexUrl: string
  loadOptions: MetadataUserEditLoadOptions
}): Promise<DocumentPageMetadataUserEditEntry[]> {
  const endpoint = `${args.rolodexUrl}/api/v2/entries/load`
  const options = {
    ...args.loadOptions,
    order_by: [{ field: 'id', descending: false }],
    offset: 0,
  }
  const count = parseRolodexCount(
    await postFetcher(`${endpoint}/count`, JSON.stringify(options), args.axios)
  )
  const entries = new Map<string, DocumentPageMetadataUserEditEntry>()
  let offset = 0
  while (entries.size < count) {
    const rows = parseRolodexRows(
      await postFetcher(
        endpoint,
        JSON.stringify({ ...options, offset }),
        args.axios
      )
    )
    const before = entries.size
    for (const row of rows) {
      const parsed = DocumentPageMetadataUserEditEntrySchema.safeParse(row)
      if (
        !parsed.success ||
        evalEventIdentity(parsed.data, undefined) === undefined
      )
        throw new Error(
          'An eval event could not be read; metrics are unavailable'
        )
      entries.set(parsed.data.entry.id, parsed.data)
    }
    offset += rows.length
    if (entries.size === before || rows.length < options.limit) break
  }
  if (entries.size < count)
    throw new Error(
      `Eval event load incomplete: loaded ${entries.size} of ${count}`
    )
  // Preserve the existing newest-first input order, including stable equal-time selection.
  return [...entries.values()].sort((a, b) =>
    (b.entry.created_at ?? '').localeCompare(a.entry.created_at ?? '')
  )
}
